/**
 * API Service for One Globe Alliance Members Portal
 */
import axios from 'axios';

const resolveApiBase = () => {
  const envBase = process.env.REACT_APP_BACKEND_URL || '';
  const host = window.location.hostname;
  if (host === 'oneglobealliance.com' || host === 'www.oneglobealliance.com' || host === 'uat.oneglobealliance.com' || host === 'kyc.boltcargo.com') {
    return window.location.origin;
  }
  return envBase || 'http://localhost:8001';
};

const API_BASE = resolveApiBase();

export function getApiBaseUrl() {
  return resolveApiBase();
}

/**
 * Convert HTTP backend URL to WebSocket URL (http -> ws, https -> wss)
 */
export function getWebSocketUrl() {
  const base = resolveApiBase();
  return base.replace(/^http/, 'ws');
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: single-session — clear storage and flag when logged in elsewhere
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail || '';
    const isLoggedInElsewhere =
      error.response?.status === 401 &&
      (typeof detail === 'string' && (detail.includes('elsewhere') || detail.includes('no longer valid')));
    if (isLoggedInElsewhere) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('member');
      sessionStorage.setItem('logout_reason', 'logged_in_elsewhere');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============ DIRECTORY API ============
export const directoryApi = {
  getMembers: (params = {}) => api.get('/api/directory/members', { params }),
  getMember: (memberId) => api.get(`/api/directory/members/${memberId}`),
  getCompanies: (params = {}) => api.get('/api/directory/companies', { params }),
  /** All approved/visible companies (from members); use for complaint form etc. */
  getCompaniesFromMembers: (params = {}) => api.get('/api/directory/companies/from-members', { params }),
  getCompany: (companyId) => api.get(`/api/directory/companies/${companyId}`),
  getPorts: () => api.get('/api/directory/ports'),
  searchPorts: (q = '', limit = 30) => api.get('/api/directory/ports/search', { params: { q, limit } }),
  getPortByCode: (code) => api.get(`/api/directory/ports/by-code/${encodeURIComponent(code)}`),
  getPorFpod: () => api.get('/api/directory/por-fpod'),
  searchPorFpod: (q = '', limit = 30) => api.get('/api/directory/por-fpod/search', { params: { q, limit } }),
  getPorFpodByCode: (code) => api.get(`/api/directory/por-fpod/by-code/${encodeURIComponent(code)}`),
  getAirports: () => api.get('/api/directory/airports'),
  searchAirports: (q = '', limit = 30) => api.get('/api/directory/airports/search', { params: { q, limit } }),
  getAirportByCode: (code) => api.get(`/api/directory/airports/by-code/${encodeURIComponent(code)}`),
  getCountries: () => api.get('/api/directory/countries'),
  getCities: (country) => api.get(`/api/directory/cities/${encodeURIComponent(country)}`),
  getRatingCriteria: () => api.get('/api/directory/rating-criteria'),
  setPreferredPartner: (memberId, partnerId, serviceType) =>
    api.post('/api/directory/preferred-partners', null, {
      params: { member_id: memberId, partner_id: partnerId, service_type: serviceType }
    }),
  removePreferredPartner: (memberId, partnerId, serviceType) =>
    api.delete('/api/directory/preferred-partners', {
      params: { member_id: memberId, partner_id: partnerId, service_type: serviceType }
    }),
};

// ============ QUOTES API ============
export const quotesApi = {
  getQuotes: (params = {}) => api.get('/api/quotes', { params }),
  getQuote: (quoteId) => api.get(`/api/quotes/${quoteId}`),
  getAvailableQuotes: (memberId, params = {}) =>
    api.get('/api/quotes/received/available', { params: { member_id: memberId, ...params } }),
  createQuote: (quoteData, memberId) =>
    api.post('/api/quotes', quoteData, { params: { member_id: memberId } }),
  updateDraftQuote: (quoteId, quoteData, memberId) =>
    api.put(`/api/quotes/${quoteId}/draft`, quoteData, { params: { member_id: memberId } }),
  publishDraftQuote: (quoteId, quoteData, memberId) =>
    api.post(`/api/quotes/${quoteId}/publish`, quoteData, { params: { member_id: memberId } }),
  updatePostedInquiry: (quoteId, quoteData) =>
    api.put(`/api/quotes/${quoteId}`, quoteData),
  updateQuote: (quoteId, updates) => api.put(`/api/quotes/${quoteId}`, updates),
  updateQuoteStatus: (quoteId, status, feedback = null) =>
    api.put(`/api/quotes/${quoteId}/status`, null, { params: { status, feedback } }),
  updatePublicSharing: (quoteId, enabled) =>
    api.put(`/api/quotes/${quoteId}/public-sharing`, { enabled }),

  // Line items and T&Cs
  addLineItem: (quoteId, lineItem, memberId) =>
    api.post(`/api/quotes/${quoteId}/add-line-item`, lineItem, { params: { member_id: memberId } }),
  addTargetRate: (quoteId, targetRate, memberId) =>
    api.post(`/api/quotes/${quoteId}/add-target-rate`, targetRate, { params: { member_id: memberId } }),
  addTcClause: (quoteId, clause, memberId) =>
    api.post(`/api/quotes/${quoteId}/add-tc-clause`, clause, { params: { member_id: memberId } }),

  // Priority and promotion
  promoteQuote: (quoteId, memberId) =>
    api.post(`/api/quotes/${quoteId}/promote`, null, { params: { member_id: memberId } }),
  changePriority: (quoteId, priority, memberId, priorityUntil = null) =>
    api.put(`/api/quotes/${quoteId}/priority`, null, {
      params: { priority, member_id: memberId, ...(priorityUntil && { priority_until: priorityUntil }) },
    }),
  toggleVoid: (quoteId, memberId) =>
    api.put(`/api/quotes/${quoteId}/void`, null, { params: { member_id: memberId } }),
  deleteDraftQuote: (quoteId, memberId) =>
    api.delete(`/api/quotes/${quoteId}/draft`, { params: { member_id: memberId } }),
  assignQuote: (quoteId, assignedToMemberId, memberId) =>
    api.post(`/api/quotes/${quoteId}/assign`, { assigned_to_member_id: assignedToMemberId }, { params: { member_id: memberId } }),

  // Bids
  getQuoteBids: (quoteId) => api.get(`/api/quotes/${quoteId}/bids`),
  createBid: (quoteId, bidData, memberId) =>
    api.post(`/api/quotes/${quoteId}/bids`, bidData, { params: { member_id: memberId } }),
  updateBid: (quoteId, bidId, bidData, memberId) =>
    api.put(`/api/quotes/${quoteId}/bids/${bidId}`, bidData, { params: { member_id: memberId } }),
  addBidLineItem: (quoteId, bidId, lineItem, memberId) =>
    api.post(`/api/quotes/${quoteId}/bids/${bidId}/add-line-item`, lineItem, { params: { member_id: memberId } }),
  respondToTargetRate: (quoteId, bidId, response, explanation, memberId) =>
    api.put(`/api/quotes/${quoteId}/bids/${bidId}/target-response`, null, {
      params: { response, explanation, member_id: memberId }
    }),
  respondToTcClause: (quoteId, bidId, clauseId, status, remarks, memberId) =>
    api.put(`/api/quotes/${quoteId}/bids/${bidId}/tc-response`, null, {
      params: { clause_id: clauseId, status, remarks, member_id: memberId }
    }),

  // Nomination
  nominateBid: (quoteId, bidId, reason, rating, memberId, reasonCategory = null, otherReason = null) =>
    api.post(`/api/quotes/${quoteId}/nominate`, null, {
      params: { bid_id: bidId, reason, rating, member_id: memberId, reason_category: reasonCategory, other_reason: otherReason }
    }),

  // Post-nomination RFQ / payment flow
  rfqComplete: (quoteId, memberId) =>
    api.post(`/api/quotes/${quoteId}/rfq-complete`, null, { params: { member_id: memberId } }),
  rfqAccept: (quoteId, memberId) =>
    api.post(`/api/quotes/${quoteId}/rfq-accept`, null, { params: { member_id: memberId } }),
  rfqReopen: (quoteId, memberId) =>
    api.post(`/api/quotes/${quoteId}/rfq-reopen`, null, { params: { member_id: memberId } }),
  sendQuoteReminder: (quoteId, memberId) =>
    api.post(`/api/quotes/${quoteId}/send-reminder`, null, { params: { member_id: memberId } }),
  submitQuotePaymentDetails: (quoteId, body, memberId) =>
    api.post(`/api/quotes/${quoteId}/payment-details`, body, { params: { member_id: memberId } }),
  getPaymentBankDetails: (memberId) =>
    api.get('/api/quotes/payment-bank-details', { params: { member_id: memberId } }),
  getQuotePayment: (quoteId) => api.get(`/api/quotes/${quoteId}/payment`),
  getPaymentProof: (quoteId) => api.get(`/api/quotes/${quoteId}/payment-proof`),
  paymentReceived: (quoteId, memberId, providerRating = null, providerFeedback = null) =>
    api.post(`/api/quotes/${quoteId}/payment-received`, { provider_rating: providerRating, provider_feedback: providerFeedback }, { params: { member_id: memberId } }),

  // Completion
  completeQuote: (quoteId, requesterRating, requesterFeedback, providerRating, providerFeedback, memberId) =>
    api.post(`/api/quotes/${quoteId}/complete`, null, {
      params: {
        requester_rating: requesterRating,
        requester_feedback: requesterFeedback,
        provider_rating: providerRating,
        provider_feedback: providerFeedback,
        member_id: memberId
      }
    }),

  // Reference data
  getUnits: () => api.get('/api/quotes/reference/units'),
  getCommodities: () => api.get('/api/quotes/reference/commodities'),
  getIncoterms: () => api.get('/api/quotes/reference/incoterms'),
  getContainerTypes: () => api.get('/api/quotes/reference/container-types'),
  getTaxTypes: () => api.get('/api/quotes/reference/tax-types'),
  getChargeCodes: () => api.get('/api/quotes/reference/charge-codes'),
  getPointsConfig: () => api.get('/api/quotes/reference/points-config'),
};

// Public inquiry requests intentionally omit the member bearer token so shared
// links remain usable on mobile and in logged-out browser sessions.
export const publicInquiryApi = {
  getInquiry: (shareToken) =>
    axios.get(
      `${API_BASE}/api/public/inquiries/${encodeURIComponent(shareToken)}`,
      { timeout: 30000 }
    ),
};

// ============ MEMBER LEADERBOARD API ============
export const leaderboardApi = {
  getLeaderboard: (category = 'quotation_value', period = 'monthly') =>
    api.get('/api/leaderboard', { params: { category, period } }),
};

export const exchangeRatesApi = {
  getLatest: () => api.get('/api/exchange-rates/latest'),
};

// ============ EVENTS API ============
export const eventsApi = {
  getEvents: (params = {}) => api.get('/api/events', { params }),
  getUpcomingEvents: () => api.get('/api/events/upcoming'),
  getEvent: (eventId) => api.get(`/api/events/${eventId}`),
  createEvent: (eventData) => api.post('/api/events', eventData),

  // Registration
  registerForEvent: (eventId, registrationData) =>
    api.post(`/api/events/${eventId}/register`, registrationData),
  checkRegistration: (eventId, memberId) =>
    api.get(`/api/events/${eventId}/registrations/${memberId}`),

  // Meeting requests
  createMeetingRequest: (eventId, requestData) =>
    api.post(`/api/events/${eventId}/meeting-requests`, requestData),
  getMeetingRequests: (eventId, memberId = null) =>
    api.get(`/api/events/${eventId}/meeting-requests`, { params: { member_id: memberId } }),
  acceptMeetingRequest: (eventId, requestId) =>
    api.put(`/api/events/${eventId}/meeting-requests/${requestId}/accept`),
  declineMeetingRequest: (eventId, requestId, reason = null) =>
    api.put(`/api/events/${eventId}/meeting-requests/${requestId}/decline`, null, { params: { reason } }),

  // Scheduled meetings
  getScheduledMeetings: (eventId, memberId = null) =>
    api.get(`/api/events/${eventId}/meetings`, { params: { member_id: memberId } }),
  getMeeting: (eventId, meetingId) =>
    api.get(`/api/events/${eventId}/meetings/${meetingId}`),
  updateMeeting: (eventId, meetingId, updates) =>
    api.put(`/api/events/${eventId}/meetings/${meetingId}`, updates),
  addMeetingNotes: (eventId, meetingId, notes) =>
    api.post(`/api/events/${eventId}/meetings/${meetingId}/notes`, null, { params: { notes } }),
  addMeetingTodo: (eventId, meetingId, todo) =>
    api.post(`/api/events/${eventId}/meetings/${meetingId}/todos`, todo),

  // Photos
  getEventPhotos: (eventId) => api.get(`/api/events/${eventId}/photos`),
  addEventPhoto: (eventId, photoUrl) =>
    api.post(`/api/events/${eventId}/photos`, null, { params: { photo_url: photoUrl } }),
  getSponsorships: (eventId) => api.get(`/api/events/${eventId}/sponsorships`),
  checkoutSponsorships: (eventId, payload) => api.post(`/api/events/${eventId}/sponsorships/checkout`, payload),
  getMemberSponsorshipPayment: (eventId, memberId) =>
    api.get(`/api/events/${eventId}/sponsorship-payments/${memberId}`),
  uploadSponsorshipPaymentProof: (eventId, paymentId, payload) =>
    api.patch(`/api/events/${eventId}/sponsorship-payments/${paymentId}/proof`, payload),
};

// ============ CHAT API ============
export const chatApi = {
  getConversations: (memberId) => api.get('/api/chat/conversations', { params: { member_id: memberId } }),
  getConversation: (conversationId) => api.get(`/api/chat/conversations/${conversationId}`),
  createConversation: (conversationData) => api.post('/api/chat/conversations', conversationData),

  // Messages
  getMessages: (conversationId, limit = 100, before = null) =>
    api.get(`/api/chat/conversations/${conversationId}/messages`, { params: { limit, before } }),
  sendMessage: (conversationId, messageData) =>
    api.post(`/api/chat/conversations/${conversationId}/messages`, messageData),
  markAsRead: (conversationId, memberId) =>
    api.put(`/api/chat/conversations/${conversationId}/read`, null, { params: { member_id: memberId } }),

  // Quote chat (requester-bidder pair)
  getQuoteChat: (quoteId, memberId = null, participantId = null) =>
    api.get(`/api/chat/quote/${quoteId}`, {
      params: {
        ...(memberId ? { member_id: memberId } : {}),
        ...(participantId ? { participant_id: participantId } : {}),
      }
    }),
  createQuoteChat: (quoteId, participants, referenceNumber = null) =>
    api.post(`/api/chat/quote/${quoteId}`, { participants, reference_number: referenceNumber || undefined }),
  openBidQuoteChat: (quoteId, bidId) =>
    api.post(`/api/chat/quote/${quoteId}/bid/${bidId}`),

  // Group chat
  createGroupChat: (title, participants, creatorId) =>
    api.post('/api/chat/groups', null, { params: { title, creator_id: creatorId }, data: participants }),

  // Search
  searchConversations: (memberId, query) =>
    api.get('/api/chat/search', { params: { member_id: memberId, query } }),
};

export const memberActivityApi = {
  recordSectionView: (section) => api.post('/api/member-activity/section-view', { section }),
};

// ============ M&A API ============
export const maApi = {
  getListings: (memberId, params = {}) =>
    api.get('/api/ma/listings', { params: { member_id: memberId, ...params } }),
  getListing: (listingId, memberId) =>
    api.get(`/api/ma/listings/${listingId}`, { params: { member_id: memberId } }),
  createListing: (listingData, memberId) =>
    api.post('/api/ma/listings', listingData, { params: { member_id: memberId } }),
  updateListing: (listingId, updates, memberId) =>
    api.put(`/api/ma/listings/${listingId}`, updates, { params: { member_id: memberId } }),

  // Enquiries
  expressInterest: (listingId, enquiryData, memberId) =>
    api.post(`/api/ma/listings/${listingId}/enquire`, enquiryData, { params: { member_id: memberId } }),
  getListingEnquiries: (listingId, memberId) =>
    api.get(`/api/ma/listings/${listingId}/enquiries`, { params: { member_id: memberId } }),
  getMyEnquiries: (memberId) => api.get('/api/ma/my-enquiries', { params: { member_id: memberId } }),
};

// ============ PAYMENTS API ============
export const paymentsApi = {
  getConfig: () => api.get('/api/payments/config'),
  getProducts: () => api.get('/api/payments/products'),
  createRecord: (data) => api.post('/api/payments/create-record', data),
  bankTransferRequest: (paymentId) => api.post('/api/payments/bank-transfer/request', { payment_id: paymentId }),

  // Stripe
  createStripeCheckout: (data) => api.post('/api/payments/stripe/create-checkout-session', data),

  // PayPal
  createPayPalOrder: (data) => api.post('/api/payments/paypal/create-order', data),
  executePayPalOrder: (paymentId, payerId) =>
    api.post('/api/payments/paypal/execute-order', { payment_id: paymentId, payer_id: payerId }),

  // Razorpay
  createPaymentLink: (data) => api.post('/api/payments/create-payment-link', data),
  createEventPaymentLink: (data) => api.post('/api/payments/event/create-payment-link', data),
  createRazorpayOrder: (data) => api.post('/api/payments/razorpay/create-order', data),
  verifyRazorpayPayment: (data) => api.post('/api/payments/razorpay/verify-payment', data),

  // Bank Transfer
  initiateBankTransfer: (data) => api.post('/api/payments/bank-transfer/initiate', data),
  uploadBankTransferProof: (paymentId, proofUrl) =>
    api.post(`/api/payments/bank-transfer/${paymentId}/upload-proof`, { proof_url: proofUrl }),
  submitBankTransferDetails: (paymentId, data) =>
    api.post('/api/payments/bank-transfer/submit-details', { payment_id: paymentId, ...data }),

  // History
  getPaymentHistory: (params = {}) => api.get('/api/payments/history', { params }),
  getPayment: (paymentId) => api.get(`/api/payments/${paymentId}`),
};

// ============ COLLEAGUES API ============
export const colleaguesApi = {
  getColleagues: (companyId) => api.get('/api/colleagues', { params: { company_id: companyId } }),
  inviteColleague: (colleagueData, inviterId) =>
    api.post('/api/colleagues/invite', colleagueData, { params: { inviter_id: inviterId } }),
  updateColleague: (colleagueId, updates, adminId) =>
    api.put(`/api/colleagues/${colleagueId}`, updates, { params: { admin_id: adminId } }),
  removeColleague: (colleagueId, adminId) =>
    api.delete(`/api/colleagues/${colleagueId}`, { params: { admin_id: adminId } }),
  setAdminRights: (colleagueId, hasAdminRights, adminId) =>
    api.put(`/api/colleagues/${colleagueId}/admin-rights`, null, {
      params: { has_admin_rights: hasAdminRights, admin_id: adminId }
    }),
  setNominationRights: (colleagueId, canNominate, adminId) =>
    api.put(`/api/colleagues/${colleagueId}/nomination-rights`, null, {
      params: { can_nominate: canNominate, admin_id: adminId }
    }),
};

// ============ REFERRALS API ============
export const referralsApi = {
  getReferrals: (referrerId) => api.get('/api/referrals', { params: { referrer_id: referrerId } }),
  createReferral: (referralData) => api.post('/api/referrals', referralData),
  getReferral: (referralId) => api.get(`/api/referrals/${referralId}`),
};

// ============ COMPLAINTS API ============
export const complaintsApi = {
  getComplaints: (memberId, params = {}) =>
    api.get('/api/complaints', { params: { member_id: memberId, ...params } }),
  getStats: (memberId) => api.get('/api/complaints/stats', { params: { member_id: memberId } }),
  fileComplaint: (complaintData, memberId) =>
    api.post('/api/complaints', complaintData, { params: { member_id: memberId } }),
  getComplaint: (complaintId, memberId) =>
    api.get(`/api/complaints/${complaintId}`, { params: { member_id: memberId } }),
  resolveComplaint: (complaintId, resolutionData, memberId) =>
    api.put(`/api/complaints/${complaintId}/resolve`, resolutionData, { params: { member_id: memberId } }),
  updateComplaintStatus: (complaintId, status, adminNotes = null) =>
    api.put(`/api/complaints/${complaintId}/status`, null, { params: { status, admin_notes: adminNotes } }),
  addComment: (complaintId, comment, memberId) =>
    api.post(`/api/complaints/${complaintId}/comment`, null, { params: { comment, member_id: memberId } }),
  submitGrievance: (data, memberId) =>
    api.post('/api/complaints/grievance', data, { params: { member_id: memberId } }),
};

// ============ NOTIFICATIONS API ============
export const notificationsApi = {
  getNotifications: (memberId, params = {}) =>
    api.get('/api/notifications', { params: { member_id: memberId, ...params } }),
  getUnreadCount: (memberId) => api.get('/api/notifications/count', { params: { member_id: memberId } }),
  markAsRead: (notificationId, memberId) =>
    api.put(`/api/notifications/${notificationId}/read`, null, { params: { member_id: memberId } }),
  markAllAsRead: (memberId) => api.put('/api/notifications/read-all', null, { params: { member_id: memberId } }),
  deleteNotification: (notificationId, memberId) =>
    api.delete(`/api/notifications/${notificationId}`, { params: { member_id: memberId } }),
};

// ============ PROFILE API ============
export const profileApi = {
  getProfile: (memberId) => api.get(`/api/profile/${memberId}`),
  updateProfile: (memberId, updates) => api.put(`/api/profile/${memberId}`, updates),
  getCompanyBankDetails: (memberId) => api.get(`/api/profile/${memberId}/bank-details`),
  createCompanyBankDetail: (memberId, payload) => api.post(`/api/profile/${memberId}/bank-details`, payload),
  updateCompanyBankDetail: (memberId, detailId, payload) => api.put(`/api/profile/${memberId}/bank-details/${detailId}`, payload),
  deleteCompanyBankDetail: (memberId, detailId) => api.delete(`/api/profile/${memberId}/bank-details/${detailId}`),
  uploadPresentation: (memberId, fileUrl) =>
    api.post(`/api/profile/${memberId}/presentation`, null, { params: { file_url: fileUrl } }),
  getTierConfig: (memberId) => api.get(`/api/profile/${memberId}/tier-config`),
  getTiers: () => api.get('/api/tiers'),
};

// ============ COMPANY REGISTRATION API ============
export const companyRegistrationApi = {
  getMyRegistration: () => api.get('/api/company-registration/my-registration'),
  updateBankAccountNumber: (accountNumber) =>
    api.patch('/api/company-registration/my-registration/bank-account-number', {
      account_number: accountNumber,
    }),
  updateLinkedinOnboardingConsent: (consent) =>
    api.patch('/api/company-registration/my-registration/linkedin-onboarding-consent', {
      consent,
    }),
};

// ============ AUTHENTICATION API ============
export const authApi = {
  register: (userData) => api.post('/api/auth/register', userData),
  getReferralContext: (referralCode) => api.get('/api/auth/referral-context', { params: { ref: referralCode } }),
  login: (credentials) => api.post('/api/auth/login', credentials),
  logout: () => api.post('/api/auth/logout'),
  getCurrentUser: () => api.get('/api/auth/me'),
  changePassword: (passwords) => api.put('/api/auth/change-password', passwords),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) =>
    api.post('/api/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  verifyToken: () => api.get('/api/auth/verify-token'),
  verifyEmail: (token) => api.get('/api/auth/verify-email', { params: { token } }),
  resendVerification: (email) => api.post('/api/auth/resend-verification', null, { params: { email } }),
};

// ============ TEAM MANAGEMENT API ============
export const teamApi = {
  getRoles: () => api.get('/api/team/roles'),
  getTeamMembers: (companyId, includeInactive = false) =>
    api.get('/api/team', { params: { company_id: companyId, include_inactive: includeInactive } }),
  getTeamStats: (companyId) => api.get('/api/team/stats', { params: { company_id: companyId } }),
  inviteTeamMember: (memberData, adminMemberId) =>
    api.post('/api/team/invite', memberData, { params: { admin_member_id: adminMemberId } }),
  getTeamMember: (teamMemberId) => api.get(`/api/team/${teamMemberId}`),
  updateTeamMember: (teamMemberId, updates, adminMemberId) =>
    api.put(`/api/team/${teamMemberId}`, updates, { params: { admin_member_id: adminMemberId } }),
  updatePermissions: (teamMemberId, permissions, adminMemberId) =>
    api.put(`/api/team/${teamMemberId}/permissions`, permissions, { params: { admin_member_id: adminMemberId } }),
  removeTeamMember: (teamMemberId, adminMemberId) =>
    api.delete(`/api/team/${teamMemberId}`, { params: { admin_member_id: adminMemberId } }),
  resendInvite: (teamMemberId, adminMemberId) =>
    api.post(`/api/team/${teamMemberId}/resend-invite`, null, { params: { admin_member_id: adminMemberId } }),
};

// ============ ANALYTICS API ============
export const analyticsApi = {
  getOverview: (memberId) => api.get('/api/analytics/overview', { params: { member_id: memberId } }),
  getCompanyOverview: (memberId) => api.get('/api/analytics/company-overview', { params: { member_id: memberId } }),
  getQuotationByStep: (memberId, scope = 'individual') =>
    api.get('/api/analytics/quotation-by-step', { params: { member_id: memberId, scope } }),
  getQuotesActivity: (memberId, months = 6) =>
    api.get('/api/analytics/quotes-activity', { params: { member_id: memberId, months } }),
  getDealsByRegion: (memberId) => api.get('/api/analytics/deals-by-region', { params: { member_id: memberId } }),
  getTopPartners: (memberId, limit = 10) =>
    api.get('/api/analytics/top-partners', { params: { member_id: memberId, limit } }),
  getQuoteTypes: (memberId) => api.get('/api/analytics/quote-types', { params: { member_id: memberId } }),
  getEventParticipation: (memberId) => api.get('/api/analytics/event-participation', { params: { member_id: memberId } }),
  getPerformanceTrend: (memberId, days = 30) =>
    api.get('/api/analytics/performance-trend', { params: { member_id: memberId, days } }),
};

// ============ GLOBAL RFQ API ============
export const globalRfqApi = {
  getRFQs: (params = {}) => api.get('/api/global-rfq', { params }),
  getRFQ: (rfqId) => api.get(`/api/global-rfq/${rfqId}`),
  createRFQ: (rfqData) => api.post('/api/global-rfq', rfqData),
  assignMembersToLeg: (rfqId, legId, memberIds) =>
    api.post(`/api/global-rfq/${rfqId}/legs/${legId}/assign`, memberIds),
  nominateForLeg: (rfqId, legId, memberId) =>
    api.post(`/api/global-rfq/${rfqId}/legs/${legId}/nominate`, null, { params: { member_id: memberId } }),
  updateRFQStatus: (rfqId, status) =>
    api.put(`/api/global-rfq/${rfqId}/status`, null, { params: { status } }),
};

// ============ MEMBER STORIES API ============
export const memberStoriesApi = {
  list: (mine = false) => api.get('/api/member-stories', { params: { mine } }),
  submit: (data) => api.post('/api/member-stories', data),
  toggleLike: (storyId) => api.post(`/api/member-stories/${storyId}/like`),
  comment: (storyId, content) => api.post(`/api/member-stories/${storyId}/comments`, { content }),
  pending: () => api.get('/api/member-stories/admin/pending'),
  review: (storyId, approved) => api.post(`/api/member-stories/admin/${storyId}/review`, null, { params: { approved } }),
};

// ============ FILE UPLOADS API ============
export const uploadsApi = {
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/uploads/profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Single file upload
  uploadFile: (file, category = 'general', relatedId = null, memberId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (relatedId) formData.append('related_id', relatedId);
    if (memberId) formData.append('member_id', memberId);

    return api.post('/api/uploads/single', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Multiple files upload
  uploadMultiple: (files, category = 'general', relatedId = null, memberId = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('category', category);
    if (relatedId) formData.append('related_id', relatedId);
    if (memberId) formData.append('member_id', memberId);

    return api.post('/api/uploads/multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  uploadQuoteDocuments: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/api/uploads/quote-documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Get file URL
  getFileUrl: (category, filename) => `${API_BASE}/api/uploads/files/${category}/${filename}`,

  // Delete file
  deleteFile: (category, filename, memberId = null) =>
    api.delete(`/api/uploads/files/${category}/${filename}`, { params: { member_id: memberId } }),

  // List files in category
  listFiles: (category, memberId = null) =>
    api.get(`/api/uploads/list/${category}`, { params: { member_id: memberId } }),
};

export default api;
